import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { consultationService } from '../../../services/consultationService';
import { parseApiDate, formatDate } from '../../../lib/format';
import type { MedicalConsultation } from '../../../types/api';
import { getPrescriptionTemplate } from '../print-prescription/registry';
import { resolveConsultationSpecialtyKey } from '../print-prescription/specialtyKeys';
import type { PrescriptionPrintContext } from '../print-prescription/types';
import { useClinicSettings } from '../../../hooks/useClinicSettings';
import { usePrintGate } from '../hooks/usePrintGate';
import { activePrescriptions } from '../hooks/correctionAccess';

// This age is printed on the prescription, so it must not drift: a
// date-only birth date parsed as UTC lands a day earlier, which changes
// the year for anyone born on the 1st of January.
const calcAge = (dob?: string) => {
  const birth = parseApiDate(dob);
  if (!birth) return null;
  return Math.floor((Date.now() - birth.getTime()) / (1000 * 60 * 60 * 24 * 365.25));
};

const fmt = (d?: string) =>
  formatDate(d, { day: '2-digit', month: 'long', year: 'numeric' });

const PrescriptionPrintPage = () => {
  const { id } = useParams<{ id: string }>();
  const [c, setC] = useState<MedicalConsultation | null>(null);
  const [error, setError] = useState('');
  // When a letterhead PDF has been uploaded, the doctor can toggle
  // whether to print on it (the print-ON-letterhead flow) or skip it
  // (the print-on-blank flow). Defaults to ON so the preview matches
  // the actual print output.
  const [includeLetterhead, setIncludeLetterhead] = useState<boolean>(true);
  const { clinicName, clinicAddress, logoUrl, letterheadUrl } = useClinicSettings(c?.doctor_id);
  // The sign response carries only patient + doctor (no signer, no
  // prescriptions or other children), so printing it would drop the
  // delegation line's assistant name and the record's content. Reload the
  // full consultation first, as ConsultationDetailPage does.
  const reloadAfterSign = useCallback(() => {
    consultationService.getById(Number(id))
      .then(setC)
      .catch(() => setError('Error cargando la consulta.'));
  }, [id]);
  const { isBlocked, canSign, signing, signError, sign } = usePrintGate(c, reloadAfterSign);

  useEffect(() => {
    consultationService.getById(Number(id))
      .then(setC)
      .catch(() => setError('Error cargando la consulta.'));
  }, [id]);

  useEffect(() => {
    if (!c || isBlocked) return;
    const t = setTimeout(() => window.print(), 600);
    return () => clearTimeout(t);
  }, [c, isBlocked]);

  if (error) return <div style={{ padding: 40, color: 'red' }}>{error}</div>;
  if (!c) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif' }}>
      <span>Cargando receta...</span>
    </div>
  );

  // clinical-record-signing R8: a draft prescription is never printable,
  // even through this client-side render path (must-fix item c).
  if (isBlocked) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif', gap: 12 }}>
        <p>Esta receta pertenece a una consulta pendiente de firma y no puede imprimirse.</p>
        {canSign ? (
          <button className="btn btn-primary" disabled={signing} onClick={() => sign()}>
            {signing ? 'Firmando...' : 'Firmar consulta'}
          </button>
        ) : (
          <p><em>Pendiente de firma.</em></p>
        )}
        {signError && <p style={{ color: 'red' }}>{signError}</p>}
      </div>
    );
  }

  const specialtyKey = resolveConsultationSpecialtyKey(c);
  const Template = getPrescriptionTemplate(specialtyKey);

  const printContext: PrescriptionPrintContext = {
    consultation: c,
    doctor: c.doctor ?? null,
    patient: c.patient ?? null,
    prescriptions: activePrescriptions(c.prescriptions),
    specialtyKey,
    specialtyData: (c.specialty_data as Record<string, unknown> | undefined) ?? null,
  };

  return (
    <>
      {letterheadUrl && (
        <div
          className="no-print"
          style={{
            position: 'fixed',
            top: 12,
            right: 12,
            zIndex: 10,
            background: 'white',
            border: '1px solid #d1d5db',
            borderRadius: 4,
            padding: '6px 12px',
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            fontFamily: 'sans-serif',
            fontSize: 13,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeLetterhead}
              onChange={(e) => setIncludeLetterhead(e.target.checked)}
            />
            Imprimir sobre membrete
          </label>
        </div>
      )}

      <Template
        {...printContext}
        clinicName={clinicName}
        clinicAddress={clinicAddress}
        clinicLogo={logoUrl}
        letterheadUrl={letterheadUrl}
        includeLetterhead={includeLetterhead}
        formatDate={fmt}
        calculateAge={calcAge}
      />
    </>
  );
};

export default PrescriptionPrintPage;
